import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { founderTypes } from '../data/founderTypes';
import { getResults, calculateProfile } from '../utils/profileCalculator';
import { decodeResultsShareParam } from '../utils/shareLinkUtils';
import { calculateArchetypes } from '../utils/archetypeCalculator';
import { getLatestCheckIn } from '../utils/checkInStorage';
import { addResultsRun, getResultsHistory, renameResultsRun } from '../utils/resultsHistoryStorage';
import { trackEvent } from '../utils/analytics';
import { applyTypeTheme, clearTypeTheme } from '../utils/founderIdentity';

const normalizeProfileTypes = (p) => {
  if (!p) return p;
  const next = { ...p };
  if (next.primaryType) next.primaryType = String(next.primaryType).toLowerCase();
  if (next.secondaryType) next.secondaryType = String(next.secondaryType).toLowerCase();
  return next;
};

export const useResultsProfile = () => {
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [latestCheckIn, setLatestCheckIn] = useState(null);
  const [historyRuns, setHistoryRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);

  useEffect(() => {
    const resultsData = getResults();
    const profileData = resultsData?.profile || null;
    const shareParam = new URLSearchParams(location.search).get('share');
    const shared = decodeResultsShareParam(shareParam);

    if (profileData && profileData.primaryType) {
      profileData.primaryType = profileData.primaryType.toLowerCase();
      if (profileData.secondaryType) {
        profileData.secondaryType = profileData.secondaryType.toLowerCase();
      }

      const storedAnswers = Array.isArray(resultsData?.answers) ? resultsData.answers : [];
      if (storedAnswers.length > 0 && !Array.isArray(profileData.drivers)) {
        const recalculated = calculateProfile(storedAnswers);
        profileData.drivers = recalculated.drivers;
        profileData.confidence = profileData.confidence || recalculated.confidence;
      }

      setProfile(profileData);
      setCreatedAt(resultsData?.createdAt || null);
      setLatestCheckIn(getLatestCheckIn());

      let history = getResultsHistory();
      if (history.length === 0 && resultsData?.createdAt) {
        addResultsRun({
          profile: profileData,
          answers: storedAnswers,
          createdAt: resultsData.createdAt,
          schemaVersion: resultsData?.schemaVersion || 3,
        });
        history = getResultsHistory();
      }
      setHistoryRuns(history);
      setSelectedRunId(history?.[0]?.id || null);

      trackEvent('results_viewed', {
        primaryType: profileData.primaryType,
        secondaryType: profileData.secondaryType || null,
      });
    } else if (shared?.p && shared?.pct) {
      const normalizedPrimary = String(shared.p).toLowerCase();
      const normalizedSecondary = shared.s ? String(shared.s).toLowerCase() : null;

      setProfile({
        primaryType: normalizedPrimary,
        secondaryType: normalizedSecondary,
        percentages: shared.pct,
        confidence: shared.conf || { label: 'low', marginPercent: 0 },
        scoringVersion: shared.sv || 1,
        compatibleTypes: founderTypes[normalizedPrimary]?.compatibleWith || [],
        archetypes: calculateArchetypes(shared.pct),
      });
      setCreatedAt(null);
      setLatestCheckIn(getLatestCheckIn());
      setHistoryRuns([]);
      setSelectedRunId(null);

      trackEvent('results_viewed', {
        primaryType: normalizedPrimary,
        secondaryType: normalizedSecondary,
        source: 'share_link',
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (profile?.primaryType) applyTypeTheme(profile.primaryType);
    return () => clearTypeTheme();
  }, [profile?.primaryType]);

  const handleViewRun = (run) => {
    if (!run) return;
    setSelectedRunId(run.id);
    setProfile(normalizeProfileTypes(run.profile));
    setCreatedAt(run.createdAt || null);
  };

  const handleHistoryNameChange = (runId, nextName) => {
    setHistoryRuns((prev) => prev.map((r) => (r.id === runId ? { ...r, name: nextName } : r)));
  };

  const handleHistoryNameCommit = (runId, name) => {
    setHistoryRuns(renameResultsRun(runId, name));
  };

  const refreshFromStorage = () => {
    const resultsData = getResults();
    const profileData = resultsData?.profile || null;
    if (profileData?.primaryType) {
      profileData.primaryType = String(profileData.primaryType).toLowerCase();
      if (profileData.secondaryType) {
        profileData.secondaryType = String(profileData.secondaryType).toLowerCase();
      }
      setProfile(profileData);
      setCreatedAt(resultsData?.createdAt || null);
    }
    setLatestCheckIn(getLatestCheckIn());
    setHistoryRuns(getResultsHistory());
  };

  return {
    profile,
    createdAt,
    latestCheckIn,
    historyRuns,
    selectedRunId,
    handleViewRun,
    handleHistoryNameChange,
    handleHistoryNameCommit,
    refreshFromStorage,
  };
};

export default useResultsProfile;
